import { useState, useRef, useEffect } from "react";
import { Mic, Square, Send, X, Loader2, Play, Pause } from "lucide-react";
import { Button } from "@/components/ui/button";

export function VoiceRecorder({ onSend, onCancel, disabled }) {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [duration, setDuration] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const audioRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    return () => {
      // Cleanup on unmount
      if (timerRef.current) clearInterval(timerRef.current);
      if (audioUrl) URL.revokeObjectURL(audioUrl);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, [audioUrl]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4'
      });
      
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const mimeType = mediaRecorder.mimeType || 'audio/webm';
        const blob = new Blob(audioChunksRef.current, { type: mimeType });
        setAudioBlob(blob);
        const url = URL.createObjectURL(blob);
        setAudioUrl(url);
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start(100); // Collect data every 100ms
      setIsRecording(true);
      setDuration(0);
      
      // Start timer
      timerRef.current = setInterval(() => {
        setDuration(prev => prev + 1);
      }, 1000);
      
    } catch (err) {
      console.error("Error accessing microphone:", err);
      alert("Impossible d'acceder au microphone. Verifie les permissions.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  const cancelRecording = () => {
    stopRecording();
    setAudioBlob(null);
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
      setAudioUrl(null);
    }
    setDuration(0);
    onCancel?.();
  };

  const togglePlayback = () => {
    if (!audioRef.current) return;
    
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleSend = () => {
    if (audioBlob) {
      // Create a file from the blob
      const ext = audioBlob.type.includes('webm') ? 'webm' : 'm4a';
      const file = new File([audioBlob], `vocal_${Date.now()}.${ext}`, { type: audioBlob.type });
      onSend?.(file, duration);
      
      // Reset state
      setAudioBlob(null);
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
        setAudioUrl(null);
      }
      setDuration(0);
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Recording state - show stop button
  if (isRecording) {
    return (
      <div className="flex items-center gap-3 p-3 bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-800 animate-pulse">
        <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
        <span className="text-sm font-medium text-red-600 dark:text-red-400">
          Enregistrement... {formatDuration(duration)}
        </span>
        <div className="flex-1" />
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={cancelRecording}
          className="text-red-500 hover:text-red-700 hover:bg-red-100"
        >
          <X className="w-4 h-4" />
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={stopRecording}
          className="bg-red-500 hover:bg-red-600 text-white"
        >
          <Square className="w-4 h-4 mr-1" />
          Stop
        </Button>
      </div>
    );
  }

  // Recorded audio - show preview and send
  if (audioBlob && audioUrl) {
    return (
      <div className="flex items-center gap-3 p-3 bg-[#16233B]/5 dark:bg-[#16233B]/20 rounded-xl border border-[#16233B]/20 dark:border-[#16233B]/40">
        <audio 
          ref={audioRef} 
          src={audioUrl} 
          onEnded={() => setIsPlaying(false)}
          className="hidden"
        />
        
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={togglePlayback}
          className="text-[#16233B] dark:text-white hover:bg-[#16233B]/10"
        >
          {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
        </Button>
        
        {/* Waveform placeholder */}
        <div className="flex-1 flex items-center gap-0.5 h-8">
          {Array.from({ length: 30 }).map((_, i) => (
            <div
              key={i}
              className="w-1 bg-[#16233B]/40 dark:bg-[#16233B] rounded-full"
              style={{ 
                height: `${Math.random() * 100}%`,
                minHeight: '4px'
              }}
            />
          ))}
        </div>
        
        <span className="text-xs text-slate-500 dark:text-slate-400 font-mono">
          {formatDuration(duration)}
        </span>
        
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={cancelRecording}
          className="text-slate-400 hover:text-red-500"
        >
          <X className="w-4 h-4" />
        </Button>
        
        <Button
          type="button"
          size="sm"
          onClick={handleSend}
          disabled={disabled}
          className="bg-[#16233B] hover:bg-[#162B47] text-white"
        >
          {disabled ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              <Send className="w-4 h-4 mr-1" />
              Envoyer
            </>
          )}
        </Button>
      </div>
    );
  }

  // Default state - show record button
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      onClick={startRecording}
      disabled={disabled}
      title="Enregistrer un message vocal"
      className="text-[#6B7280] hover:text-[#16233B] hover:bg-[#16233B]/5 shrink-0"
    >
      <Mic className="w-4 h-4" strokeWidth={1.5} />
    </Button>
  );
}
